import { useCallback, useEffect, useRef, useState } from 'react';
import { integrationService } from '@/services';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import type { MetaAuthType } from '../meta-contract';

/**
 * What the Meta OAuth popup posts back to the window that opened it.
 *
 * Machine's callback page renders nothing of its own — it `postMessage`s this to `window.opener`
 * and closes. The `source` tag is what lets the opener tell it apart from every other message on
 * the page (extensions, embedded widgets, devtools), and a payload that does not match this shape
 * is ignored rather than guessed at.
 */
export const META_OAUTH_MESSAGE_SOURCE = 'kim-meta-oauth';

export interface MetaOAuthMessage {
	source: typeof META_OAUTH_MESSAGE_SOURCE;
	status: 'success' | 'error';
	message?: string;
}

const DEFAULT_ERROR_MESSAGE = 'Meta connection failed';
const POPUP_BLOCKED_MESSAGE = 'Could not open the Meta window. Allow popups for this site and try again.';
const POPUP_CLOSED_POLL_MS = 500;

/** Parse an incoming `message` payload; anything that is not ours is `null`. Pure, for testing. */
export const readMetaOAuthMessage = (data: unknown): MetaOAuthMessage | null => {
	if (typeof data !== 'object' || data === null) return null;
	const candidate = data as Record<string, unknown>;
	if (candidate.source !== META_OAUTH_MESSAGE_SOURCE) return null;
	if (candidate.status !== 'success' && candidate.status !== 'error') return null;

	return {
		source: META_OAUTH_MESSAGE_SOURCE,
		status: candidate.status,
		message: typeof candidate.message === 'string' ? candidate.message : undefined
	};
};

/**
 * Only the popup we opened may report a result. `event.source` is set by the browser, not the
 * sender, so it cannot be forged by a page that merely knows the payload shape. Origin is NOT
 * checked: the callback is served by machine on a host that differs per environment, and a wrong
 * allow-list would silently swallow every result — the source identity is the stronger check.
 */
export const isFromPopup = (event: Pick<MessageEvent, 'source'>, popup: Window | null): boolean =>
	popup !== null && event.source === popup;

interface UseMetaConnectOptions {
	/** Re-read the integrations list; the hook calls it whenever a result may have landed. */
	refetchIntegrations: () => Promise<unknown>;
}

export interface UseMetaConnectResult {
	/** The route whose popup is open, or null when nothing is in flight. */
	pendingAuthType: MetaAuthType | null;
	connect: (authType: MetaAuthType) => Promise<void>;
	/** Drop the in-flight state by hand — the card's "Check connection status" uses it on success. */
	clearPending: () => void;
}

/**
 * The Meta connect flow: open the consent popup, then wait for its verdict.
 *
 * Three signals end the wait, any of which may arrive first:
 *  - the popup's `postMessage` — the authoritative result, success or failure with a reason;
 *  - the popup closing — the customer gave up, or the message was lost; the list is refetched so
 *    a connection that did land still shows, and the buttons are released either way;
 *  - the app window regaining focus — refetch only, because focus says nothing about the outcome.
 *
 * Pending used to be keyed on the channel count going up, which never happened when the customer
 * reconnected an account that was already listed, or simply closed the window.
 */
export const useMetaConnect = ({ refetchIntegrations }: UseMetaConnectOptions): UseMetaConnectResult => {
	const [pendingAuthType, setPendingAuthType] = useState<MetaAuthType | null>(null);
	const popupRef = useRef<Window | null>(null);

	const clearPending = useCallback(() => {
		popupRef.current = null;
		setPendingAuthType(null);
	}, []);

	const refetchSafely = useCallback(
		(reason: string) =>
			refetchIntegrations().catch((err: unknown) => {
				console.error('[useMetaConnect] refetch after popup result failed', { reason, err });
			}),
		[refetchIntegrations]
	);

	const connect = useCallback(
		async (authType: MetaAuthType) => {
			setPendingAuthType(authType);

			let popup: Window | null = null;
			try {
				popup = await integrationService.connectMetaIntegration(authType);
			} catch (err) {
				console.error('[useMetaConnect.connect] install request failed', { authType, err });
				toast.error(getApiErrorMessage(err, 'Failed to start the Meta connection'));
				clearPending();
				return;
			}

			if (!popup) {
				// Either the browser blocked the window or machine answered without an auth URL. Both
				// leave the customer with nothing open, so say so rather than silently re-enabling.
				console.error('[useMetaConnect.connect] no popup window', { authType });
				toast.error(POPUP_BLOCKED_MESSAGE);
				clearPending();
				return;
			}

			popupRef.current = popup;
		},
		[clearPending]
	);

	const handlePopupMessage = useCallback(
		(event: MessageEvent) => {
			if (!isFromPopup(event, popupRef.current)) return;
			const result = readMetaOAuthMessage(event.data);
			if (!result) return;

			if (result.status === 'error') {
				console.error('[useMetaConnect] popup reported a failure', { message: result.message });
				toast.error(result.message || DEFAULT_ERROR_MESSAGE);
				clearPending();
				return;
			}

			void refetchSafely('popup success');
			clearPending();
		},
		[clearPending, refetchSafely]
	);

	/** The popup's verdict. */
	useEffect(() => {
		if (!pendingAuthType) return;
		window.addEventListener('message', handlePopupMessage);
		return () => window.removeEventListener('message', handlePopupMessage);
	}, [pendingAuthType, handlePopupMessage]);

	/** The popup going away without a verdict. */
	useEffect(() => {
		if (!pendingAuthType) return;

		const timer = window.setInterval(() => {
			if (!popupRef.current?.closed) return;
			void refetchSafely('popup closed');
			clearPending();
		}, POPUP_CLOSED_POLL_MS);

		return () => window.clearInterval(timer);
	}, [pendingAuthType, clearPending, refetchSafely]);

	/**
	 * Coming back to the app. The global `refetchOnWindowFocus` is PROD-only (see lib/query-client),
	 * so this is what keeps a dev build behaving the same way.
	 */
	useEffect(() => {
		if (!pendingAuthType) return;

		const onFocus = () => void refetchSafely('window focus');
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [pendingAuthType, refetchSafely]);

	return { pendingAuthType, connect, clearPending };
};
